import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'usehooks-ts'
import countries, { Alpha2Code } from 'i18n-iso-countries'
import { AxiosResponse } from 'axios'

import {
  Flex,
  FormField,
  Select,
  SelectOption,
  TextInput,
  TextArea,
  FlagAvatar,
  AsyncSelect,
  Card,
  Box,
  OrganisationHeaderWithImage,
  MoneyInput
} from '@percent/lemonade'
import { CurrencyCode, Money } from '@percent/utility'
import { loadCountryData } from '@percent/partner-dashboard/i18n/countries'
import { useAuthState, useQueryList } from '@percent/partner-dashboard/common/hooks'
import { PBPListResponse } from '@percent/partner-dashboard/services/types/pbpResponses.types'
import { OrganisationUnion } from '@percent/partner-dashboard/services/nonprofits/nonprofitsService.types'
import { selectScopes } from '@percent/partner-dashboard/common/library/utility/selectScopes'
import { PartnerScopes } from '@percent/partner-dashboard/constants/scopes'
import type { CorporateDonationFormProps } from './CorporateDonationForm.types'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'

export const GIFT_NOTE_MAX_LENGTH = 500

export function CorporateDonationForm({
  errors,
  values,
  handleChange,
  handleBlur,
  touched,
  setFieldValue,
  setFieldTouched,
  defaultCurrency,
  currencies,
  intlConfigLang,
  subsidiariesData,
  selectedOrganisation,
  setSelectedOrganisation,
  onSubsidiaryChange
}: CorporateDonationFormProps) {
  const { t } = useTranslation()
  const [countryOptions, setCountryOptions] = useState<SelectOption[]>([])

  const [query, setQuery] = useState<string>('')
  const [searchCountryCode, setSearchCountryCode] = useState<string>('')
  const { partner } = useAuthState()
  const scopes = selectScopes(partner?.scopes)
  const hasWorkplaceGivingScope = scopes[PartnerScopes.WorkplaceGiving]
  const {
    nonprofitsService: { getWorkplaceOrganisations, getOrganisations }
  } = useServices()

  const debouncedQuery = useDebounce(query, 200)

  const searchOrganisationsAPI = useCallback(
    async (params: {
      query: string
      pageSize?: number
      countryCode?: string
    }): Promise<AxiosResponse<PBPListResponse<OrganisationUnion>>> => {
      if (hasWorkplaceGivingScope) {
        return getWorkplaceOrganisations(params) as Promise<AxiosResponse<PBPListResponse<OrganisationUnion>>>
      }

      return getOrganisations(params) as Promise<AxiosResponse<PBPListResponse<OrganisationUnion>>>
    },
    [hasWorkplaceGivingScope, getWorkplaceOrganisations, getOrganisations]
  )

  const shouldSearch = debouncedQuery.length >= 2 && !!values.country
  const [{ data: searchData, isLoading }, { query: triggerSearch }] = useQueryList(
    searchOrganisationsAPI,
    { query: '', pageSize: 10, countryCode: '' },
    false
  )

  useEffect(() => {
    if (shouldSearch) {
      triggerSearch({ query: debouncedQuery, pageSize: 10, countryCode: searchCountryCode })
    }
  }, [shouldSearch, debouncedQuery, searchCountryCode, triggerSearch])

  useEffect(() => {
    loadCountryData()
    const countryNames = countries.getNames('en')
    const options = Object.entries(countryNames)
      .map(([alpha2Code, countryName]) => {
        const alpha3Code = countries.alpha2ToAlpha3(alpha2Code)

        if (!alpha3Code) return null

        return {
          value: alpha3Code,
          label: countryName,
          prefix: <FlagAvatar code={alpha2Code as Alpha2Code} />
        }
      })
      .filter(Boolean) as SelectOption[]

    setCountryOptions(options)
  }, [])

  const searchResults: SelectOption[] = useMemo(() => {
    if (!searchData) return []

    return searchData.map((organisation: OrganisationUnion) => {
      const id = 'organisationId' in organisation ? organisation.organisationId : organisation.id

      return {
        value: id,
        label: organisation.name,
        description: id
      }
    })
  }, [searchData])

  const handleCountryChange = useCallback(
    (option: SelectOption) => {
      const newValue = option?.value || ''
      setFieldValue('country', newValue)
      setSearchCountryCode(newValue)
      setFieldValue('organization', '')
      setFieldTouched('organization', false)
      setSelectedOrganisation(null)
      setQuery('')
    },
    [setFieldTouched, setFieldValue, setSelectedOrganisation]
  )

  const handleSelectOrgChange = (option: SelectOption | null) => {
    setFieldValue('organization', option?.value ?? '')
    setFieldTouched('organization')

    if (option && searchData) {
      const selectedOrg = searchData.find((org: OrganisationUnion) => {
        const id = 'organisationId' in org ? org.organisationId : org.id

        return id === option.value
      })
      setSelectedOrganisation(selectedOrg || null)
    } else {
      setSelectedOrganisation(null)
    }
  }

  const handleQueryChange = (queryValue: string) => {
    setQuery(queryValue)
    setFieldTouched('organization')
    setFieldValue('organization', queryValue)
    setSelectedOrganisation(null)
  }

  const subsidiaryOptions: SelectOption[] = useMemo(() => {
    const subsidiaryOptionsList =
      subsidiariesData?.map(subsidiary => ({
        value: subsidiary.id,
        label: subsidiary.name
      })) ?? []

    if (subsidiaryOptionsList.length > 0) {
      return [
        ...subsidiaryOptionsList,
        {
          value: '',
          label: t('corporateDonation.field.subsidiary.none')
        }
      ]
    }

    return subsidiaryOptionsList
  }, [subsidiariesData, t])

  const handleAmountChange = (
    value: Money | { amount: undefined; currency: CurrencyCode } | undefined,
    _raw?: string | undefined
  ) => {
    if (value?.amount != null && value.amount > 0) {
      setFieldValue('amount', value.amount)
    } else if (value === undefined) {
      setFieldValue('amount', undefined)
    } else if (value?.amount === 0) {
      setFieldValue('amount', 0)
    }

    if (value?.currency) {
      setFieldValue('currency', value.currency)
    }
  }

  return (
    <Flex direction="column" gap={20}>
      <FormField
        label={t('corporateDonation.field.country')}
        necessity="required"
        status={touched.country && errors.country ? 'danger' : 'default'}
        statusMessage={errors.country}
        data-testid="country-select"
      >
        <Select
          name="country"
          placeholder={t('corporateDonation.field.country.placeholder')}
          options={countryOptions}
          defaultValue={countryOptions.find(opt => opt.value === values.country) || undefined}
          onChange={handleCountryChange}
          status={touched.country && errors.country ? 'danger' : 'default'}
          searchable
          showPrefixForSelectedOption
          key={values.country || 'no-country'}
        />
      </FormField>

      <FormField
        label={t('corporateDonation.field.organization')}
        necessity="required"
        status={touched.organization && errors.organization ? 'danger' : 'default'}
        statusMessage={errors.organization}
        description={selectedOrganisation ? t('typography.organisationDetailsDescription') : ''}
        data-testid="organization-select"
      >
        <>
          <AsyncSelect
            name="organization"
            key={searchCountryCode}
            placeholder={
              !values.country
                ? t('corporateDonation.field.organization.selectFirst')
                : t('corporateDonation.field.organization.placeholder')
            }
            onChange={handleSelectOrgChange}
            options={searchResults}
            setQuery={handleQueryChange}
            query={query}
            loading={isLoading && !!values.country}
            loadingText={t('typography.searching')}
            noResultsFoundText={query.length >= 2 ? t('typography.noSearchResults') : t('typography.typeAtLeast2Chars')}
            status={touched.organization && errors.organization ? 'danger' : 'default'}
            disabled={!values.country}
          />
          {selectedOrganisation && (
            <Card>
              <Box
                style={{
                  padding: '1.5rem 1.5rem 0 1.5rem',
                  width: '100%'
                }}
                data-testid="organisationDetailsPreview"
              >
                <OrganisationHeaderWithImage
                  title={selectedOrganisation.displayName || selectedOrganisation.name}
                  countryCode={selectedOrganisation.countryCode}
                  registryId={
                    'organisationIdInRegistry' in selectedOrganisation
                      ? selectedOrganisation.organisationIdInRegistry
                      : selectedOrganisation.registryId
                  }
                  iconUrl={'logoUrl' in selectedOrganisation ? selectedOrganisation.logoUrl : selectedOrganisation.logo}
                  website={selectedOrganisation.website}
                />
              </Box>
            </Card>
          )}
        </>
      </FormField>

      {subsidiaryOptions.length > 0 && (
        <FormField
          label={t('corporateDonation.field.subsidiary')}
          status={touched.subsidiary && errors.subsidiary ? 'danger' : 'default'}
          statusMessage={errors.subsidiary}
          data-testid="subsidiary-select"
        >
          <Select
            name="subsidiary"
            placeholder={t('corporateDonation.field.subsidiary.placeholder')}
            options={subsidiaryOptions}
            defaultValue={subsidiaryOptions.find(opt => opt.value === values.subsidiary) || undefined}
            onChange={option => {
              const newValue = option?.value || ''

              if (onSubsidiaryChange) {
                onSubsidiaryChange(newValue)
              } else {
                setFieldValue('subsidiary', newValue)
                setFieldTouched('subsidiary', true)
              }
            }}
            status={touched.subsidiary && errors.subsidiary ? 'danger' : 'default'}
          />
        </FormField>
      )}

      <FormField
        label={t('corporateDonation.field.amount')}
        necessity="required"
        status={touched.amount && errors.amount ? 'danger' : 'default'}
        statusMessage={errors.amount}
        data-testid="amount-input"
      >
        <MoneyInput
          id="amount"
          name="amount"
          defaultCurrency={defaultCurrency}
          locale={intlConfigLang}
          currencies={currencies}
          value={values.amount > 0 ? { amount: values.amount, currency: values.currency } : undefined}
          placeholder={{ amount: 0, currency: defaultCurrency }}
          key={defaultCurrency}
          onValueChange={handleAmountChange}
          onBlur={handleBlur}
          allowNegativeValue={false}
        />
      </FormField>

      <FormField
        label={t('corporateDonation.field.costCenter')}
        status={touched.costCenter && errors.costCenter ? 'danger' : 'default'}
        statusMessage={errors.costCenter}
        data-testid="cost-center-input"
      >
        <TextInput
          name="costCenter"
          placeholder={t('corporateDonation.field.costCenter.placeholder')}
          value={values.costCenter}
          onChange={handleChange}
          onBlur={handleBlur}
          status={touched.costCenter && errors.costCenter ? 'danger' : 'default'}
        />
      </FormField>

      <FormField
        label={t('corporateDonation.field.giftNote')}
        status={touched.giftNote && errors.giftNote ? 'danger' : 'default'}
        statusMessage={errors.giftNote}
      >
        <TextArea
          name="giftNote"
          placeholder={t('corporateDonation.field.giftNote.placeholder')}
          value={values.giftNote}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={GIFT_NOTE_MAX_LENGTH}
          status={touched.giftNote && errors.giftNote ? 'danger' : 'default'}
          data-testid="gift-note-input"
        />
      </FormField>
    </Flex>
  )
}
